import * as React from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Avatar from "@mui/material/Avatar";
import TablePagination from "@mui/material/TablePagination";
import { alpha } from "@mui/material/styles";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { LoadingPage } from "../../components/LoadingPage/LoadingPage";
import { SnackbarApiError } from "../../components/SnackbarApiError/SnackbarApiError";
import { PowerBiReportSummary, PowerBiService } from "../../gen/clients/llts";
import { useNavigation } from "../../hooks/useNavigation";
import { ROUTES } from "../../utils/navigationUtils";

const DEFAULT_PAGE_SIZE = 10;

const PowerBiReportsPage: React.FC = () => {
  const { navigateTo } = useNavigation();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE);

  const {
    data: reports,
    isLoading,
    error
  } = useQuery<PowerBiReportSummary[]>("listPowerBiReports", {
    queryFn: PowerBiService.listPowerBiReports
  });

  const pageData = React.useMemo(() => {
    if (!reports) {
      return [];
    }
    const startIndex = currentPage * rowsPerPage;
    return reports.slice(startIndex, startIndex + rowsPerPage);
  }, [reports, currentPage, rowsPerPage]);

  const onPageChange = React.useCallback((_: unknown, newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const onRowsPerPageChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setCurrentPage(0);
  }, []);

  const breadcrumbs = React.useMemo(
    () => [
      {
        label: t("crumbText.home"),
        path: "/",
        active: false
      },
      {
        label: t("pbiReports.title"),
        path: "/pbiReports",
        active: true
      }
    ],
    [t]
  );

  const onViewReportClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      navigateTo(`${ROUTES.PBI_REPORTS}/${event.currentTarget.id}`);
    },
    [navigateTo]
  );

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <Container maxWidth="xl" sx={{ my: 4 }}>
      {error && <SnackbarApiError error={error} />}
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <Stack
        spacing={3}
        sx={{
          mt: 1,
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
          backgroundImage: theme =>
            `radial-gradient(1200px circle at 0% 0%, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 55%),` +
            `radial-gradient(800px circle at 100% 0%, ${alpha(theme.palette.primary.main, 0.04)} 0%, transparent 45%)`,
          boxShadow: "0 12px 30px rgba(17, 24, 39, 0.08)"
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
          {t("pbiReports.title")}
        </Typography>
        {reports && reports.length > 0 ? (
          <Box>
            <List
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                bgcolor: "transparent"
              }}
              disablePadding={true}
            >
              {pageData.map((report: PowerBiReportSummary) => (
                <React.Fragment key={report.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      id={report.id}
                      onClick={onViewReportClick}
                      sx={{
                        minHeight: 80,
                        py: 2,
                        px: 2.5,
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                        transition: "transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 20px rgba(17, 24, 39, 0.12)",
                          borderColor: "primary.light",
                          backgroundColor: "background.default"
                        }
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          variant="rounded"
                          sx={{
                            bgcolor: theme => alpha(theme.palette.primary.main, 0.12),
                            border: "1px solid",
                            borderColor: theme => alpha(theme.palette.primary.main, 0.24),
                            width: 44,
                            height: 44
                          }}
                        >
                          <img src="/images/services/pbiReports.svg" style={{ width: 24 }} alt="Power BI" />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={report.name}
                        secondary={report.description}
                        primaryTypographyProps={{ variant: "button", sx: { fontWeight: 600, letterSpacing: 0.3 } }}
                        secondaryTypographyProps={{
                          variant: "body2",
                          color: "text.secondary",
                          sx: { mt: 0.5 }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
            <TablePagination
              component="div"
              count={reports.length}
              page={currentPage}
              onPageChange={onPageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onRowsPerPageChange}
              showFirstButton={true}
              showLastButton={true}
            />
          </Box>
        ) : (
          <Typography variant="body1">{t("pbiReports.emptyState")}</Typography>
        )}
      </Stack>
    </Container>
  );
};

export { PowerBiReportsPage };
